import { generateId } from './contentUtils';
import { ensureReferenceListBlockExists } from './apaReferenceListUtils';

/**
 * BOPPPS Slide Template
 * A comprehensive template showcasing various content blocks across all BOPPPS sections
 * Includes multi-column cards, preset layouts, and diverse block combinations
 */
export const createSlideTemplate = (options = {}) => {
  const template = {
  title: "BOPPPS Lesson Template",
  description: "A comprehensive template showcasing various content blocks and layouts",
  context: "This template demonstrates the use of different content blocks available in the BOPPPS framework",
  exportBaseName: "boppps_template",
  exportVersionLabel: "2.0",
  sections: [
    // Bridge-In Section
    {
      id: 'bridge-in',
      title: 'Bridge-In',
      type: 'boppps',
      blocks: [
        // Title slide preset
        {
          id: generateId(),
          type: 'headline',
          content: '<h1 style="font-size: 3rem; text-align: center; margin: 2rem 0;">Welcome to Today\'s Lesson</h1>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p style="font-size: 1.5rem; text-align: center; color: #64748b;">Connecting Previous Knowledge to New Concepts</p>'
        },
        {
          id: generateId(),
          type: 'divider'
        },
        // Multi-column comparison layout
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>What We Know vs. What We\'ll Learn</h2>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Previous Knowledge',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Foundation Concepts',
                  content: '<ul><li>Basic understanding of core principles</li><li>Familiarity with key terminology</li><li>Prior experience with similar topics</li></ul>',
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                  borderColor: '#0ea5e9',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'New Learning',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Advanced Applications',
                  content: '<ul><li>Deep dive into advanced techniques</li><li>Real-world problem solving</li><li>Integration with modern practices</li></ul>',
                  backgroundColor: '#dcfce7',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        {
          id: generateId(),
          type: 'exercise-box',
          style: 'exercise',
          content: '<p><strong>Reflection Activity:</strong> Take 2-3 minutes to write down what you already know about this topic. What questions do you hope to answer today?</p>'
        }
      ]
    },
    // Learning Outcomes Section
    {
      id: 'outcomes',
      title: 'Learning Outcomes',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Learning Outcomes</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>By the end of this lesson, you will be able to:</p>'
        },
        // Three-column layout with numbered objectives
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: '',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  primaryTitle: '1',
                  title: 'Understand',
                  content: '<p>Comprehend fundamental concepts and their relationships</p>',
                  backgroundColor: '#fef3c7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true,
                  titleAlignment: 'center'
                }
              ]
            },
            {
              id: generateId(),
              title: '',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  primaryTitle: '2',
                  title: 'Apply',
                  content: '<p>Demonstrate proficiency through practical exercises and examples</p>',
                  backgroundColor: '#ddd6fe',
                  textColor: '#4c1d95',
                  borderColor: '#8b5cf6',
                  showBorder: true,
                  titleAlignment: 'center'
                }
              ]
            },
            {
              id: generateId(),
              title: '',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  primaryTitle: '3',
                  title: 'Create',
                  content: '<p>Design original solutions using learned techniques</p>',
                  backgroundColor: '#fed7e2',
                  textColor: '#831843',
                  borderColor: '#ec4899',
                  showBorder: true,
                  titleAlignment: 'center'
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        {
          id: generateId(),
          type: 'info-box',
          style: 'info',
          content: '<p><strong>Learning Path:</strong> These outcomes build progressively from understanding to creation, following Bloom\'s Taxonomy.</p>'
        }
      ]
    },
    // Pre-Assessment Section
    {
      id: 'pre-assessment',
      title: 'Pre-Assessment',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Check Your Current Knowledge</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Before we dive deeper, let\'s assess your starting point:</p>'
        },
        // Two-column checklist layout
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Self-Assessment Checklist',
              width: '60%',
              widthPercent: 60,
              cards: [
                {
                  id: generateId(),
                  title: 'Rate Your Familiarity',
                  content: '',
                  isChecklist: true,
                  checklistItems: [
                    { id: generateId(), text: 'I understand the basic terminology', checked: false },
                    { id: generateId(), text: 'I can explain key concepts to others', checked: false },
                    { id: generateId(), text: 'I have practical experience with applications', checked: false },
                    { id: generateId(), text: 'I can identify common challenges', checked: false },
                    { id: generateId(), text: 'I\'m ready to learn advanced techniques', checked: false }
                  ],
                  checkmarkColor: '#10b981',
                  backgroundColor: '#f0fdf4',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Reflection Notes',
              width: '40%',
              widthPercent: 40,
              cards: [
                {
                  id: generateId(),
                  title: 'Your Thoughts',
                  content: '<p style="color: #64748b; font-style: italic; padding: 1rem;">Take a moment to note any specific areas where you\'d like to focus your learning today. What are your main questions or concerns?</p>',
                  backgroundColor: '#fef9e7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        {
          id: generateId(),
          type: 'cards',
          layout: '2x2',
          style: 'info',
          items: [
            {
              id: generateId(),
              title: 'Question 1',
              content: '<p>What is your current understanding of the main concept?</p>'
            },
            {
              id: generateId(),
              title: 'Question 2',
              content: '<p>Have you encountered similar topics before?</p>'
            },
            {
              id: generateId(),
              title: 'Question 3',
              content: '<p>What challenges do you anticipate?</p>'
            },
            {
              id: generateId(),
              title: 'Question 4',
              content: '<p>What are your learning goals for this session?</p>'
            }
          ]
        }
      ]
    },
    // Participatory Learning Section - 6+ slides with various blocks
    {
      id: 'participatory-learning',
      title: 'Participatory Learning',
      type: 'boppps',
      blocks: [
        // Slide 1: Title/Text/Image preset layout
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: '',
              width: '42%',
              widthPercent: 42,
              cards: [
                {
                  id: generateId(),
                  title: '',
                  content: '<h2 style="font-size: 1.875rem; font-weight: 600; color: #1f2937; margin-bottom: 1rem;">Core Concepts Introduction</h2><p style="color: #6b7280; font-size: 1rem; line-height: 1.6;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Vestibulum tortor quam, feugiat vitae, ultricies eget, tempor sit amet, ante.</p>',
                  backgroundColor: '#ffffff',
                  textColor: '#1f2937',
                  borderColor: 'transparent'
                }
              ]
            },
            {
              id: generateId(),
              title: '',
              width: '58%',
              widthPercent: 58,
              cards: [
                {
                  id: generateId(),
                  title: '',
                  content: '<div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 3rem; border-radius: 0.5rem; color: white; text-align: center; min-height: 250px; display: flex; align-items: center; justify-content: center;"><p style="font-size: 1.5rem; font-weight: 600;">Visual Content Placeholder<br/><span style="font-size: 1rem; opacity: 0.9;">Replace with image or diagram</span></p></div>',
                  backgroundColor: '#ffffff',
                  textColor: '#1f2937',
                  borderColor: 'transparent'
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        // Slide 2: Multi-column with detailed breakdown
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Detailed Concept Breakdown</h2>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Definition',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  title: 'What is it?',
                  content: '<p>Donec sed odio dui. Etiam porta sem malesuada magna mollis euismod. Nullam id dolor id nibh ultricies vehicula ut id elit.</p>',
                  backgroundColor: '#eff6ff',
                  textColor: '#1e3a8a',
                  borderColor: '#3b82f6',
                  showBorder: true
                },
                {
                  id: generateId(),
                  title: 'Key Terms',
                  content: '<ul style="margin: 0; padding-left: 1.5rem;"><li>Term Alpha</li><li>Term Beta</li><li>Term Gamma</li></ul>',
                  backgroundColor: '#f0f9ff',
                  textColor: '#0c4a6e',
                  borderColor: '#0ea5e9',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Application',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  title: 'How to use it?',
                  content: '<p>Maecenas sed diam eget risus varius blandit sit amet non magna. Cras mattis consectetur purus sit amet fermentum.</p>',
                  backgroundColor: '#f0fdf4',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                },
                {
                  id: generateId(),
                  title: 'Best Practices',
                  content: '<ul style="margin: 0; padding-left: 1.5rem;"><li>Practice One</li><li>Practice Two</li><li>Practice Three</li></ul>',
                  backgroundColor: '#ecfdf5',
                  textColor: '#065f46',
                  borderColor: '#10b981',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Examples',
              width: '33.33%',
              widthPercent: 33.33,
              cards: [
                {
                  id: generateId(),
                  title: 'Real-world cases',
                  content: '<p>Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Aenean lacinia bibendum nulla sed consectetur.</p>',
                  backgroundColor: '#fef3c7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true
                },
                {
                  id: generateId(),
                  title: 'Use Cases',
                  content: '<ul style="margin: 0; padding-left: 1.5rem;"><li>Case Study 1</li><li>Case Study 2</li><li>Case Study 3</li></ul>',
                  backgroundColor: '#fef9e7',
                  textColor: '#854d0e',
                  borderColor: '#eab308',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        // Slide 3: Cards in Grid Layout
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Key Learning Points</h2>'
        },
        {
          id: generateId(),
          type: 'cards',
          layout: '3x2',
          style: 'success',
          items: [
            {
              id: generateId(),
              title: 'Point 1',
              content: '<p>Donec id elit non mi porta gravida at eget metus. Fusce dapibus, tellus ac cursus commodo.</p>'
            },
            {
              id: generateId(),
              title: 'Point 2',
              content: '<p>Cras justo odio, dapibus ac facilisis in, egestas eget quam. Nullam quis risus eget urna mollis.</p>'
            },
            {
              id: generateId(),
              title: 'Point 3',
              content: '<p>Maecenas sed diam eget risus varius blandit sit amet non magna.</p>'
            },
            {
              id: generateId(),
              title: 'Point 4',
              content: '<p>Integer posuere erat a ante venenatis dapibus posuere velit aliquet.</p>'
            },
            {
              id: generateId(),
              title: 'Point 5',
              content: '<p>Cum sociis natoque penatibus et magnis dis parturient montes.</p>'
            },
            {
              id: generateId(),
              title: 'Point 6',
              content: '<p>Aenean lacinia bibendum nulla sed consectetur.</p>'
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        // Slide 4: Comparison layout with multi-column
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Approach Comparison</h2>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Traditional Approach',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Characteristics',
                  content: '<ul><li>Established methods</li><li>Proven track record</li><li>Widely understood</li><li>Well-documented</li></ul>',
                  backgroundColor: '#fef2f2',
                  textColor: '#7f1d1d',
                  borderColor: '#ef4444',
                  showBorder: true
                },
                {
                  id: generateId(),
                  title: 'Trade-offs',
                  content: '<p style="font-size: 0.9rem; color: #991b1b;">May lack flexibility in modern contexts. Requires adaptation for current needs.</p>',
                  backgroundColor: '#fee2e2',
                  textColor: '#991b1b',
                  borderColor: '#f87171',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Modern Approach',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Characteristics',
                  content: '<ul><li>Innovative techniques</li><li>Adaptive methods</li><li>Technology-enhanced</li><li>Future-focused</li></ul>',
                  backgroundColor: '#eff6ff',
                  textColor: '#1e3a8a',
                  borderColor: '#3b82f6',
                  showBorder: true
                },
                {
                  id: generateId(),
                  title: 'Trade-offs',
                  content: '<p style="font-size: 0.9rem; color: #1e40af;">Requires new learning curves. May need validation through practice.</p>',
                  backgroundColor: '#dbeafe',
                  textColor: '#1e40af',
                  borderColor: '#60a5fa',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        // Slide 5: Step-by-step with stacked cards
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Step-by-Step Implementation Process</h2>'
        },
        {
          id: generateId(),
          type: 'stacked-cards',
          items: [
            {
              id: generateId(),
              title: 'Step 1: Preparation & Planning',
              content: '<p>Vestibulum id ligula porta felis euismod semper. Donec ullamcorper nulla non metus auctor fringilla. Identify your objectives and gather necessary resources.</p>',
              sideText: '15 min',
              backgroundColor: '#eff6ff',
              textColor: '#1e3a8a',
              borderColor: '#3b82f6',
              showBorder: true
            },
            {
              id: generateId(),
              title: 'Step 2: Initial Implementation',
              content: '<p>Nullam id dolor id nibh ultricies vehicula ut id elit. Cras mattis consectetur purus sit amet fermentum. Begin with small, manageable steps.</p>',
              sideText: '30 min',
              backgroundColor: '#f0fdf4',
              textColor: '#14532d',
              borderColor: '#22c55e',
              showBorder: true
            },
            {
              id: generateId(),
              title: 'Step 3: Testing & Evaluation',
              content: '<p>Morbi leo risus, porta ac consectetur ac, vestibulum at eros. Praesent commodo cursus magna. Assess results and gather feedback.</p>',
              sideText: '20 min',
              backgroundColor: '#fef3c7',
              textColor: '#78350f',
              borderColor: '#f59e0b',
              showBorder: true
            },
            {
              id: generateId(),
              title: 'Step 4: Refinement & Optimization',
              content: '<p>Etiam porta sem malesuada magna mollis euismod. Make adjustments based on evaluation and iterate for improvement.</p>',
              sideText: '25 min',
              backgroundColor: '#fef2f2',
              textColor: '#7f1d1d',
              borderColor: '#ef4444',
              showBorder: true
            },
            {
              id: generateId(),
              title: 'Step 5: Documentation & Sharing',
              content: '<p>Document your process, outcomes, and insights. Share knowledge with your team or community for collective learning.</p>',
              sideText: '15 min',
              backgroundColor: '#f5f3ff',
              textColor: '#4c1d95',
              borderColor: '#8b5cf6',
              showBorder: true
            }
          ]
        },
        {
          id: generateId(),
          type: 'exercise-box',
          style: 'exercise',
          content: '<p><strong>Practice Activity:</strong> Apply these five steps to a current project or challenge. Document each phase and note what works well and what needs adjustment.</p>'
        },
        {
          id: generateId(),
          type: 'divider'
        },
        // Slide 6: Image Gallery and visual examples
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Visual Examples & Demonstrations</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Explore these visual representations to better understand the concepts in action:</p>'
        },
        {
          id: generateId(),
          type: 'gallery',
          images: [
            {
              id: generateId(),
              url: 'https://via.placeholder.com/600x400/4A90E2/FFFFFF?text=Example+1',
              caption: 'Visual Example 1: Lorem ipsum dolor sit amet'
            },
            {
              id: generateId(),
              url: 'https://via.placeholder.com/600x400/7ED321/FFFFFF?text=Example+2',
              caption: 'Visual Example 2: Consectetur adipiscing elit'
            },
            {
              id: generateId(),
              url: 'https://via.placeholder.com/600x400/F5A623/FFFFFF?text=Example+3',
              caption: 'Visual Example 3: Sed do eiusmod tempor'
            },
            {
              id: generateId(),
              url: 'https://via.placeholder.com/600x400/E94B3C/FFFFFF?text=Example+4',
              caption: 'Visual Example 4: Incididunt ut labore'
            }
          ],
          layout: 'grid',
          columns: 2
        },
        {
          id: generateId(),
          type: 'info-box',
          style: 'info',
          content: '<p><strong>Pro Tip:</strong> Take time to examine each example carefully. Consider how they relate to the concepts discussed and how you might apply similar approaches in your own work.</p>'
        },
        {
          id: generateId(),
          type: 'divider'
        },
        // Slide 7: Advanced techniques with multi-column
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Advanced Techniques & Pro Tips</h2>'
        },
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Technique',
              width: '40%',
              widthPercent: 40,
              cards: [
                {
                  id: generateId(),
                  categoryLabel: 'ADVANCED',
                  title: 'Optimization Strategy',
                  content: '<p>Praesent commodo cursus magna, vel scelerisque nisl consectetur et. Vivamus sagittis lacus vel augue laoreet rutrum faucibus dolor auctor.</p>',
                  backgroundColor: '#faf5ff',
                  textColor: '#581c87',
                  borderColor: '#a855f7',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Implementation',
              width: '60%',
              widthPercent: 60,
              cards: [
                {
                  id: generateId(),
                  title: 'Step-by-Step Guide',
                  numberedList: [
                    { id: generateId(), text: 'Analyze your current workflow and identify bottlenecks' },
                    { id: generateId(), text: 'Research best practices and proven methodologies' },
                    { id: generateId(), text: 'Implement changes incrementally with testing phases' },
                    { id: generateId(), text: 'Monitor results and gather performance metrics' },
                    { id: generateId(), text: 'Iterate based on data and feedback' }
                  ],
                  numberedListStyle: 'numbered',
                  backgroundColor: '#ecfdf5',
                  textColor: '#065f46',
                  borderColor: '#10b981',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'warning-box',
          style: 'warning',
          content: '<p><strong>Important Note:</strong> Advanced techniques require solid understanding of fundamentals. Ensure you\'re comfortable with basic concepts before implementing complex strategies.</p>'
        }
      ]
    },
    // Post-Assessment Section
    {
      id: 'post-assessment',
      title: 'Post-Assessment',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Assess Your Learning</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Now that we\'ve covered the material, let\'s evaluate your understanding and progress:</p>'
        },
        // Multi-column with assessment and reflection
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Self-Assessment',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Knowledge Check',
                  isChecklist: true,
                  checklistItems: [
                    { id: generateId(), text: 'I can explain the core concepts confidently', checked: false },
                    { id: generateId(), text: 'I understand how to apply these techniques', checked: false },
                    { id: generateId(), text: 'I can identify common pitfalls and solutions', checked: false },
                    { id: generateId(), text: 'I\'m ready to practice independently', checked: false }
                  ],
                  checkmarkColor: '#10b981',
                  backgroundColor: '#f0fdf4',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Growth Areas',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Areas for Improvement',
                  content: '<p style="color: #64748b; font-style: italic; padding: 1rem;">Reflect on which concepts you\'d like to review further. What additional practice would be helpful?</p>',
                  backgroundColor: '#fef9e7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        {
          id: generateId(),
          type: 'worksheet',
          questions: [
            {
              id: generateId(),
              question: 'Explain the main concept in your own words. How would you teach it to someone else?',
              answer: ''
            },
            {
              id: generateId(),
              question: 'What are the three key takeaways from this lesson? List them in order of importance.',
              answer: ''
            },
            {
              id: generateId(),
              question: 'How would you apply this knowledge to solve a practical problem in your field?',
              answer: ''
            },
            {
              id: generateId(),
              question: 'What questions do you still have? What topics would you like to explore further?',
              answer: ''
            }
          ]
        }
      ]
    },
    // Summary & Next Steps Section
    {
      id: 'summary',
      title: 'Summary & Next Steps',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Key Takeaways & Summary</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Let\'s consolidate what we\'ve learned today:</p>'
        },
        // Multi-column summary layout
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Concepts Covered',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'Main Topics',
                  numberedList: [
                    { id: generateId(), text: 'Foundation concepts and terminology' },
                    { id: generateId(), text: 'Practical application techniques' },
                    { id: generateId(), text: 'Advanced strategies and optimization' },
                    { id: generateId(), text: 'Real-world case studies' }
                  ],
                  numberedListStyle: 'numbered',
                  backgroundColor: '#eff6ff',
                  textColor: '#1e3a8a',
                  borderColor: '#3b82f6',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Skills Developed',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  title: 'New Abilities',
                  numberedList: [
                    { id: generateId(), text: 'Critical analysis and evaluation' },
                    { id: generateId(), text: 'Problem-solving methodologies' },
                    { id: generateId(), text: 'Implementation best practices' },
                    { id: generateId(), text: 'Continuous improvement mindset' }
                  ],
                  numberedListStyle: 'numbered',
                  backgroundColor: '#f0fdf4',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        {
          id: generateId(),
          type: 'list',
          listType: 'bullet',
          items: [
            { id: generateId(), content: '<p><strong>Understanding:</strong> Core concepts and their relationships</p>' },
            { id: generateId(), content: '<p><strong>Application:</strong> Practical skills through hands-on activities</p>' },
            { id: generateId(), content: '<p><strong>Analysis:</strong> Critical thinking approaches to problem-solving</p>' },
            { id: generateId(), content: '<p><strong>Growth:</strong> Strategies for continued learning and improvement</p>' }
          ]
        },
        {
          id: generateId(),
          type: 'info-box',
          style: 'success',
          content: '<p><strong>Next Steps:</strong> Continue practicing these concepts in your daily work. Explore the additional resources provided, and prepare for our next session where we\'ll build upon this foundation with advanced applications.</p>'
        },
        {
          id: generateId(),
          type: 'warning-box',
          style: 'warning',
          content: '<p><strong>Remember:</strong> Learning is a continuous journey, not a destination. Review the material regularly, practice consistently, and don\'t hesitate to ask questions or seek clarification.</p>'
        }
      ]
    },
    // Resources & Materials Section
    {
      id: 'resources',
      title: 'Resources & Materials',
      type: 'boppps',
      blocks: [
        {
          id: generateId(),
          type: 'heading',
          content: '<h2>Additional Resources & Materials</h2>'
        },
        {
          id: generateId(),
          type: 'text',
          content: '<p>Explore these curated resources to deepen your understanding and continue your learning journey:</p>'
        },
        // Multi-column resource layout
        {
          id: generateId(),
          type: 'multi-column-cards',
          columns: [
            {
              id: generateId(),
              title: 'Learning Materials',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  categoryLabel: 'READING',
                  title: 'Recommended Texts',
                  content: '<ul><li>"Fundamentals Handbook" - Core concepts</li><li>"Advanced Techniques Guide" - Expert methods</li><li>"Case Studies Collection" - Real examples</li></ul>',
                  backgroundColor: '#eff6ff',
                  textColor: '#1e3a8a',
                  borderColor: '#3b82f6',
                  showBorder: true
                },
                {
                  id: generateId(),
                  categoryLabel: 'VIDEO',
                  title: 'Tutorial Series',
                  content: '<ul><li>Introduction series (4 videos)</li><li>Implementation walkthroughs (6 videos)</li><li>Expert interviews (3 videos)</li></ul>',
                  backgroundColor: '#fef2f2',
                  textColor: '#7f1d1d',
                  borderColor: '#ef4444',
                  showBorder: true
                }
              ]
            },
            {
              id: generateId(),
              title: 'Practice & Support',
              width: '50%',
              widthPercent: 50,
              cards: [
                {
                  id: generateId(),
                  categoryLabel: 'INTERACTIVE',
                  title: 'Practice Exercises',
                  content: '<ul><li>Beginner challenges (10 exercises)</li><li>Intermediate projects (5 projects)</li><li>Advanced scenarios (3 capstones)</li></ul>',
                  backgroundColor: '#f0fdf4',
                  textColor: '#14532d',
                  borderColor: '#22c55e',
                  showBorder: true
                },
                {
                  id: generateId(),
                  categoryLabel: 'COMMUNITY',
                  title: 'Support Channels',
                  content: '<ul><li>Discussion forum - Ask questions</li><li>Study groups - Collaborative learning</li><li>Office hours - Direct support</li></ul>',
                  backgroundColor: '#fef3c7',
                  textColor: '#78350f',
                  borderColor: '#f59e0b',
                  showBorder: true
                }
              ]
            }
          ]
        },
        {
          id: generateId(),
          type: 'divider'
        },
        {
          id: generateId(),
          type: 'table',
          content: `<table>
            <thead>
              <tr>
                <th>Resource Type</th>
                <th>Title</th>
                <th>Description</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>📚 Reading</td>
                <td>Fundamental Concepts</td>
                <td>Comprehensive guide covering basics and advanced topics</td>
                <td>2-3 hours</td>
              </tr>
              <tr>
                <td>🎥 Video</td>
                <td>Tutorial Series</td>
                <td>Step-by-step video tutorials for practical application</td>
                <td>45 min</td>
              </tr>
              <tr>
                <td>💻 Interactive</td>
                <td>Practice Exercises</td>
                <td>Hands-on activities to reinforce your learning</td>
                <td>1-2 hours</td>
              </tr>
              <tr>
                <td>📖 Reference</td>
                <td>Quick Reference Guide</td>
                <td>Handy cheat sheet for common tasks and concepts</td>
                <td>15 min</td>
              </tr>
            </tbody>
          </table>`
        },
        {
          id: generateId(),
          type: 'cards',
          layout: '2x1',
          style: 'info',
          items: [
            {
              id: generateId(),
              title: 'Online Community',
              content: '<p>Join our vibrant online community to connect with fellow learners, share insights, ask questions, and get support from peers and mentors.</p>'
            },
            {
              id: generateId(),
              title: 'Office Hours',
              content: '<p>Attend weekly office hours every Wednesday at 3 PM for personalized guidance, answers to your questions, and one-on-one support.</p>'
            }
          ]
        }
      ]
    }
  ]
  };
  return options.includeReferenceList ? { ...template, sections: ensureReferenceListBlockExists(template.sections) } : template;
};

// Export function to download the template as JSON
export const downloadTemplate = () => {
  const template = createSlideTemplate();
  const dataStr = JSON.stringify(template, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'boppps_template_v2.0.json';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
